import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import "./styles/AdminContacts.css";

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState("");

  const [newContact, setNewContact] = useState({
    name: "",
    phone_raw: "",
    email: "",
  });

  // ✅ Extract CSRF token from cookies
  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrfToken="))
      ?.split("=")[1];

    if (token) setCsrfToken(token);
  }, []);

  // ✅ Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await axios.get("/api/admin/contacts");
      setContacts(response.data.data || []);
    } catch (error) {
      console.error("🔥 Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // ✅ Add new contact
  const handleAddContact = async (e) => {
    e.preventDefault();

    if (!newContact.name.trim()) {
      return alert("Name is required.");
    }

    if (!csrfToken) return console.error("🚨 CSRF token missing.");

    try {
      await axios.post("/api/admin/contacts", newContact, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      setNewContact({ name: "", phone_raw: "", email: "" });
      fetchContacts();
    } catch (error) {
      console.error("🔥 Error adding contact:", error);
    }
  };

  // ✅ Delete contact
  const handleDeleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    if (!csrfToken) return console.error("🚨 CSRF token missing.");

    try {
      await axios.delete(`/api/admin/contacts/${id}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error("🔥 Error deleting contact:", error);
    }
  };

  // ✅ Toggle visibility
  const handleToggle = async (id, field, currentValue) => {
    if (!csrfToken) return console.error("🚨 CSRF token missing.");
    try {
      const updated = contacts.map((c) =>
        c.id === id ? { ...c, [field]: currentValue ? 0 : 1 } : c
      );
      setContacts(updated);

      await axios.put(
        `/api/admin/contacts/${id}`,
        { [field]: currentValue ? 0 : 1 },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );
    } catch (error) {
      console.error(`🔥 Error toggling ${field}:`, error);
      fetchContacts(); // revert if failed
    }
  };

  // ✅ Reorder (move up/down)
  const handleMove = async (index, direction) => {
    const newOrder = [...contacts];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    if (swapIndex < 0 || swapIndex >= contacts.length) return;

    // swap
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    setContacts(newOrder);

    try {
      await axios.put(
        "/api/admin/contacts/reorder",
        { order: newOrder.map((c) => c.id) },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );
    } catch (error) {
      console.error("🔥 Error reordering contacts:", error);
      fetchContacts();
    }
  };

  return (
    <div className="admin-messages-container">
      <h2 className="messages-title">Manage Contacts</h2>

      {/* ✅ Add New Contact Form */}
      <div className="add-contact-section">
        <h3 className="table-section-title">Add Contact</h3>
        <form className="add-contact-form" onSubmit={handleAddContact}>
          <input
            type="text"
            placeholder="Name (e.g. Larry Withrow)"
            value={newContact.name}
            onChange={(e) =>
              setNewContact({ ...newContact, name: e.target.value })
            }
            required
          />
          <input
            type="text"
            placeholder="Phone (e.g. +13045551234)"
            value={newContact.phone_raw}
            onChange={(e) =>
              setNewContact({ ...newContact, phone_raw: e.target.value })
            }
          />
          <input
            type="email"
            placeholder="Email (optional)"
            value={newContact.email}
            onChange={(e) =>
              setNewContact({ ...newContact, email: e.target.value })
            }
          />
          <button type="submit" className="btn add-btn">
            <i className="fa-solid fa-plus"></i> Add Contact
          </button>
        </form>
      </div>

      {/* ✅ Contacts Table */}
      {loading ? (
        <p className="messages-loading">Loading contacts...</p>
      ) : contacts.length === 0 ? (
        <p className="messages-empty">No contacts found.</p>
      ) : (
        <div className="table-section">
          <h3 className="table-section-title">Contacts</h3>
          <div className="table-wrapper">
            <table className="messages-table">
              <thead>
                <tr>
                  <th>Display<br/>Order</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Show Phone</th>
                  <th>Show Email</th>
                  <th>Visible</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c, index) => (
                  <tr key={c.id}>
                    <td className="order-controls">
                      <button
                        className="btn move-btn"
                        onClick={() => handleMove(index, "up")}
                        disabled={index === 0}
                      >
                        <i className="fa-solid fa-arrow-up"></i>
                      </button>
                      <button
                        className="btn move-btn"
                        onClick={() => handleMove(index, "down")}
                        disabled={index === contacts.length - 1}
                      >
                        <i className="fa-solid fa-arrow-down"></i>
                      </button>
                    </td>
                    <td>{c.name}</td>
                    <td>{c.phone_raw || "—"}</td>
                    <td>{c.email || "—"}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!c.show_phone}
                        onChange={() =>
                          handleToggle(c.id, "show_phone", c.show_phone)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!c.show_email}
                        onChange={() =>
                          handleToggle(c.id, "show_email", c.show_email)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!c.visible}
                        onChange={() => handleToggle(c.id, "visible", c.visible)}
                      />
                    </td>
                    <td className="table-actions">
                      <button
                        className="btn delete-btn"
                        onClick={() => handleDeleteContact(c.id)}
                      >
                        <i className="fa-solid fa-xmark"></i> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
